import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';

import type {
  AreaTagSet,
  AreaTagLocationOption,
  AreaTagSetReference,
  CreateAreaTagSetData,
  ProjectAreaTagLocationOptions,
  UpdateAreaTagSetData,
} from './types';

export interface ProjectLayoutOption {
  id: number;
  name: string;
  type: string;
}

interface AreaTagApiItem {
  id: number;
  name: string;
  images?: Record<string, Array<{ id: number }>>;
}

const normalizeList = <T>(data: T[] | { results?: T[] }): T[] =>
  Array.isArray(data) ? data : (data.results ?? []);

const areaTagSetsUrl = (layoutId: string, projectId: string) =>
  `area-tag-sets/?layout_id=${encodeURIComponent(layoutId)}&project_id=${encodeURIComponent(projectId)}`;

const areaTagSetUrl = (areaTagSetId: number, layoutId: string, projectId: string) =>
  `area-tag-sets/${areaTagSetId}/?layout_id=${encodeURIComponent(layoutId)}&project_id=${encodeURIComponent(projectId)}`;

export const getAreaTagSets = async (
  layoutId: string,
  projectId: string
): Promise<AreaTagSet[]> => {
  const response = await GET<AreaTagSet[]>(areaTagSetsUrl(layoutId, projectId));
  return response.data;
};

export const getProjectLayouts = async (projectId: number): Promise<ProjectLayoutOption[]> => {
  const response = await GET<
    ProjectLayoutOption[] | { layouts?: ProjectLayoutOption[]; results?: ProjectLayoutOption[] }
  >(`projects/${projectId}/layouts/?project_id=${projectId}`);
  const data = response.data;
  return Array.isArray(data) ? data : (data.layouts ?? data.results ?? []);
};

export const getProjectAreaTagLocationOptions = async (
  projectId: number,
  cachedLayouts?: ProjectLayoutOption[],
): Promise<ProjectAreaTagLocationOptions> => {
  const layouts = cachedLayouts ?? await getProjectLayouts(projectId);
  const layoutResults = await Promise.all(
    layouts.map(async (layout) => {
      const [areaTagsResponse, areaTagSetsResponse] = await Promise.all([
        GET<AreaTagApiItem[] | { results?: AreaTagApiItem[] }>(
          `area-tags/?layout_id=${layout.id}&project_id=${projectId}`
        ),
        GET<AreaTagSet[] | { results?: AreaTagSet[] }>(
          `area-tag-sets/?layout_id=${layout.id}&project_id=${projectId}`
        ),
      ]);
      const areaTagSets = normalizeList<AreaTagSet>(
        areaTagSetsResponse.data as AreaTagSet[] | { results?: AreaTagSet[] }
      );
      const setsByAreaTag = new Map<number, AreaTagSetReference[]>();
      areaTagSets.forEach((areaTagSet) => {
        areaTagSet.area_tag_ids.forEach((areaTagId) => {
          const memberships = setsByAreaTag.get(areaTagId) ?? [];
          memberships.push({ id: areaTagSet.id, name: areaTagSet.name });
          setsByAreaTag.set(areaTagId, memberships);
        });
      });

      const areaTags: AreaTagLocationOption[] = normalizeList<AreaTagApiItem>(
        areaTagsResponse.data as AreaTagApiItem[] | { results?: AreaTagApiItem[] }
      ).map((areaTag) => ({
        id: areaTag.id,
        name: areaTag.name,
        layout_id: layout.id,
        layout_name: layout.name,
        photo_ids: [
          ...new Set(
            Object.values(areaTag.images ?? {}).flatMap((photos) => photos.map((photo) => photo.id))
          ),
        ],
        area_tag_sets: setsByAreaTag.get(areaTag.id) ?? [],
      }));

      return { areaTags, areaTagSets };
    })
  );

  const areaTags = layoutResults.flatMap((result) => result.areaTags);
  const areaTagSets = layoutResults.flatMap((result) =>
    result.areaTagSets.map(({ id, name }) => ({ id, name }))
  );
  return {
    areaTags: Array.from(new Map(areaTags.map((areaTag) => [areaTag.id, areaTag])).values()),
    areaTagSets: Array.from(
      new Map(areaTagSets.map((areaTagSet) => [areaTagSet.id, areaTagSet])).values()
    ),
  };
};

export const createAreaTagSet = async (
  data: CreateAreaTagSetData,
  projectId: string
): Promise<AreaTagSet> => {
  const response = await POST(`area-tag-sets/?project_id=${encodeURIComponent(projectId)}`, data);
  return response.data;
};

export const updateAreaTagSet = async (
  areaTagSetId: number,
  data: UpdateAreaTagSetData,
  layoutId: string,
  projectId: string
): Promise<AreaTagSet> => {
  const response = await PATCH(areaTagSetUrl(areaTagSetId, layoutId, projectId), data);
  return response.data;
};

export const deleteAreaTagSet = async (
  areaTagSetId: number,
  layoutId: string,
  projectId: string
): Promise<void> => {
  await DELETE(areaTagSetUrl(areaTagSetId, layoutId, projectId));
};
